import Delete from '@mui/icons-material/Delete'
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import ConfirmationModal, {
  CustomInput,
  CustomSelect,
  selectTypes,
} from 'src/consumer/components/helperComponents'
import { FEATURE_BUNDLE_TYPES_ARRAY } from 'src/consumer/constants'
import { defaultConfirmationModalProps } from 'src/consumer/helpers/utilFunctions'

const COUNT_BASED_BUNDLE_TYPE_ID = FEATURE_BUNDLE_TYPES_ARRAY?.[0]?.id || 'count_based'

function formatDeliveryModeLabel(mode) {
  if (!mode) {
    return ''
  }
  const normalizedMode = `${mode}`.trim().toLowerCase()
  if (normalizedMode === 'aumhum' || normalizedMode === 'online' || normalizedMode === 'video-call') {
    return 'Online'
  }
  if (normalizedMode === 'offline' || normalizedMode === 'in-person') {
    return 'In Person'
  }
  if (normalizedMode === 'home-visit' || normalizedMode === 'home_visit') {
    return 'Home Visit'
  }
  return normalizedMode
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatDurationsLabel(durationsInMinutes) {
  const durationLabels = [...new Set((durationsInMinutes || []).filter(Boolean))]
    .map((duration) => `${duration} min`)
  return durationLabels.join(', ')
}

function renderBundlePlanOption(plan) {
  const title = plan?.pricingPlanTitle || plan?.title || 'Untitled appointment plan'
  const eventTypeLabels = [...new Set((plan?.eventTypeTitles || []).filter(Boolean))]
  const deliveryModeLabels = [...new Set((plan?.deliveryModes || []).filter(Boolean))]
    .map(formatDeliveryModeLabel)
    .filter(Boolean)
  const metadataLine = [
    eventTypeLabels.join(', '),
    formatDurationsLabel(plan?.durationsInMinutes),
    deliveryModeLabels.join(', '),
    plan?.pricingSummary,
  ]
    .filter(Boolean)
    .join(' • ')
  const appointmentTitles = [...new Set((plan?.sourceTitles || []).filter(Boolean))]

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{title}</Typography>
      {metadataLine ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
          {metadataLine}
        </Typography>
      ) : null}
      {appointmentTitles.length > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
          Appointment titles: {appointmentTitles.join(', ')}
        </Typography>
      ) : null}
    </Box>
  )
}

export default function FeatureBundleComponent({
  bundle,
  changeBundle,
  deleteBundle,
  disabled,
  subscriptionPlansToUse,
  bundleEditorMode = 'session',
}) {
  const [confirmationModalProps, setConfirmationModalProps] = useState(
    defaultConfirmationModalProps,
  )
  const isLegacyBundle = bundleEditorMode === 'legacy'
  const selectedPlan = (subscriptionPlansToUse || []).find(
    (plan) => (plan?.id || plan?.pricingPackId) === bundle?.pricingPackId,
  )
  const selectedPlanEventTypes = [...new Set((selectedPlan?.eventTypeTitles || []).filter(Boolean))]
  const selectedPlanDeliveryModes = [...new Set((selectedPlan?.deliveryModes || []).filter(Boolean))]
    .map(formatDeliveryModeLabel)
    .filter(Boolean)
  const selectedPlanDurationLabel = formatDurationsLabel(selectedPlan?.durationsInMinutes)

  function handleDelete() {
    setConfirmationModalProps({
      ...confirmationModalProps,
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
      onSubmitFunctions: [deleteBundle],
      action: 'delete',
      body: isLegacyBundle
        ? 'Are you sure you want to remove this appointment plan from the pack?'
        : 'Are you sure you want to remove this bundle from the list?',
      buttonColor: 'error',
    })
  }

  return (
    <>
      <Grid container spacing={2}>
        {!isLegacyBundle && (
          <Grid item xs={12} md={6}>
            <CustomSelect
              id="type"
              label="Bundle Type"
              value={bundle?.type}
              options={FEATURE_BUNDLE_TYPES_ARRAY}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.title}
              placeholder="Select bundle type..."
              onChangeEvent={changeBundle}
              disabled={disabled}
              selectType={selectTypes.string}
              required
            />
          </Grid>
        )}
        {(isLegacyBundle || bundle?.type === COUNT_BASED_BUNDLE_TYPE_ID) && (
          <Grid id="maxCountInput" item xs={12} md={6}>
            <CustomInput
              id="maxCount"
              label={isLegacyBundle ? 'Sessions Included' : 'Number of purchasable features'}
              subLabel={
                isLegacyBundle
                  ? 'How many bookings of the selected appointment plan are included in this pack?'
                  : undefined
              }
              value={bundle.maxCount}
              onChange={changeBundle}
              type="number"
              min={1}
              placeholder={isLegacyBundle ? 'Enter number of sessions...' : 'Enter a number...'}
              disabled={disabled}
              required
            />
          </Grid>
        )}
        {!isLegacyBundle && bundle?.type === FEATURE_BUNDLE_TYPES_ARRAY?.[1]?.id && (
          <Grid item xs={12} md={6}>
            <CustomInput
              id="featureAndCreditValidityInDays"
              label="Feature and Credit Validity In Days"
              value={bundle?.featureAndCreditValidityInDays}
              onChange={changeBundle}
              type="number"
              placeholder="Enter validity in days..."
              disabled={disabled}
              required
            />
          </Grid>
        )}
        {/* <Grid item xs={12} md={6}>
          <CustomReactSelect
            id="metaCategoryType"
            label="Feature Bundle Category Type"
            value={bundle?.metaCategoryType}
            options={store.metaCategoryTypes
              ?.filter((type) => type?.scope === 'app')
              ?.sort((a, b) => a?.title?.localeCompare(b?.title))}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.title}
            placeholder="Select feature bundle meta category type..."
            onChangeEvent={changeBundle}
            disabled={disabled}
            selectType={selectTypes.string}
            required
          />
        </Grid>
        {bundle?.metaCategoryType && (
          <Grid item xs={12} md={6}>
            <CustomReactSelect
              id="metaCategory"
              label="Feature Bundle Category"
              value={bundle?.metaCategory}
              options={store.categories
                ?.filter((cat) => cat?.categoryType === bundle?.metaCategoryType)
                ?.sort((a, b) => a?.title?.localeCompare(b?.title))}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.title}
              placeholder="Select feature bundle category..."
              onChangeEvent={changeBundle}
              disabled={disabled}
              selectType={selectTypes.string}
              required
            />
          </Grid>
        )}
        {bundle?.metaCategory && bundle?.metaCategoryType !== 'app-subscription' && (
          <Grid item xs={12}>
            <label>Features</label>
            <ActivityItemAsyncSelect
              isMulti
              cacheUniqs={[bundle?.metaCategoryType, bundle?.metaCategory]}
              classNamePrefix="activity-item-select"
              menuPortalTarget={document.body}
              value={bundle?.featureIds}
              loadOptions={loadCategoryTypeBasedItems}
              isDisabled={disabled}
              debounceTimeout={1000}
              additional={{
                page: 0,
                size: 200,
                metaCategoryTypeId: bundle?.metaCategoryType,
                categoryIds: bundle?.metaCategory ? [bundle?.metaCategory, ''] : undefined,
              }}
              onChange={(selVal) => changeBundle({ target: { id: 'featureIds', value: selVal } })}
            />
          </Grid>
        )} */}
        <Grid item xs={12}>
          <Grid item xs={12} md={6}>
            <CustomSelect
              id="pricingPackId"
              label={isLegacyBundle ? 'Appointment Plan' : 'Plan to give'}
              subLabel={
                isLegacyBundle
                  ? 'Choose the exact appointment pricing plan this pack should redeem against.'
                  : undefined
              }
              value={bundle?.pricingPackId}
              options={subscriptionPlansToUse}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option?.pricingPlanTitle || option?.title}
              formatOptionLabel={renderBundlePlanOption}
              placeholder={
                isLegacyBundle ? 'Select appointment pricing plan...' : 'Select subscription plan...'
              }
              onChangeEvent={changeBundle}
              disabled={disabled}
              selectType={selectTypes.string}
              required
            />
          </Grid>
          {!isLegacyBundle && bundle?.pricingPackId && (
            <Grid item xs={12} md={6}>
              <CustomSelect
                id="featureIds"
                label="Events"
                value={bundle?.featureIds}
                options={[]} //mentor events
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.title}
                placeholder="Select an event..."
                onChangeEvent={changeBundle}
                disabled={disabled}
                selectType={selectTypes.string}
                required
              />
            </Grid>
          )}
          {isLegacyBundle && selectedPlan ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 1.5,
                  border: '1px solid #d8e2f0',
                  borderRadius: '12px',
                  background: '#f8fbff',
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Selected plan details
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {selectedPlanEventTypes.map((eventTypeTitle) => (
                      <Chip
                        key={`${bundle?.pricingPackId}-${eventTypeTitle}`}
                        size="small"
                        label={eventTypeTitle}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                    {selectedPlanDurationLabel ? (
                      <Chip size="small" label={selectedPlanDurationLabel} variant="outlined" />
                    ) : null}
                    {selectedPlanDeliveryModes.map((deliveryMode) => (
                      <Chip
                        key={`${bundle?.pricingPackId}-${deliveryMode}`}
                        size="small"
                        label={deliveryMode}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                  {selectedPlan?.pricingSummary ? (
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Pricing: {selectedPlan.pricingSummary}
                    </Typography>
                  ) : null}
                  {Array.isArray(selectedPlan?.sourceTitles) && selectedPlan.sourceTitles.length > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Appointment titles: {selectedPlan.sourceTitles.join(', ')}
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            </Grid>
          ) : null}
          {/* <label>Pricing Pack Id</label>
            <AsyncPaginate
              cacheUniqs={[bundle?.featureIds]}
              classNamePrefix="activity-item-select"
              menuPortalTarget={document.body}
              value={bundle?.pricingPackId}
              loadOptions={loadOptionsForAsyncPaginate}
              debounceTimeout={500}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.title}
              additional={{
                getFunction: GetSettingsData,
                apiUrl: GENERIC_APIS.entity.getPaginated,
                otherParams: {
                  entityType: ALL_ENTITY_TYPES.consumerSubscriptionPlan,
                },
              }}
              onChange={(selVal) => changeBundle({ target: { id: 'pricingPackId', value: selVal } })}
            /> */}
        </Grid>
        {deleteBundle && !disabled && (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleDelete} color="error" startIcon={<Delete />}>
              {isLegacyBundle ? 'Remove Plan' : 'Delete Bundle'}
            </Button>
          </Grid>
        )}
      </Grid>
      <ConfirmationModal {...confirmationModalProps} newImplementation />
    </>
  )
}
